import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField
} from '@mui/material';
import clsx from 'clsx';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  LOAD_PAYOFF_AMOUNT,
  loadPayoffAmountAction,
  loadRentAfterContribution
} from 'src/store/actions/contractActions';
import MoneyMask from 'src/components/MoneyMask/MoneyMask';
import DropdownMenu from 'src/components/DropdownMenu';
import { PaymentFormList } from 'src/utils/constants/transactions';
import { DatePicker } from '@mui/x-date-pickers';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import useStyles from '../styles';
import makeInitialState from './initialState';
import validationSchema from './schema';

const StepOne = ({ data, handleNext }) => {
  const { classes } = useStyles();
  const initialState = useMemo(() => makeInitialState(data), [data]);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isLoadingPayoffAmount = useSelector(
    isLoadingSelector([LOAD_PAYOFF_AMOUNT.action])
  );

  const calculateRentAferContribution = useCallback(
    amount => {
      dispatch(loadRentAfterContribution(id, amount));
    },
    [id, dispatch]
  );

  const onNext = useCallback(
    values => {
      const payload = {
        ...values,
        paymentDate: values.paymentDate
          ? momentLocalTimezone(values.paymentDate).toDate()
          : values.paymentDate
      };

      if (values.isPayoffRequest) {
        handleNext(payload);
        return;
      }

      calculateRentAferContribution(values.amount);
      handleNext(payload);
    },
    [handleNext]
  );

  const setPayoffAmount = useCallback(
    setFieldValue => value => {
      setFieldValue('amount', value);
    },
    [id, dispatch]
  );

  const loadPayoffAmount = useCallback(
    setFieldValue => {
      dispatch(loadPayoffAmountAction(id, setPayoffAmount(setFieldValue)));
    },
    [id, dispatch]
  );

  return (
    <Formik
      initialValues={initialState}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onNext}
    >
      {({
        handleSubmit,
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <FormControlLabel
              key="payoff-request"
              control={
                <Checkbox
                  name="payoffRequest"
                  color="primary"
                  checked={values.isPayoffRequest}
                  onChange={() => {
                    setFieldValue('isPayoffRequest', !values.isPayoffRequest);
                    loadPayoffAmount(setFieldValue);
                  }}
                />
              }
              label={t('COMMON.PAYOFF_REQUEST')}
              sx={{ marginTop: 1 }}
            />

            <TextField
              InputProps={{
                autoComplete: 'off',
                inputComponent: MoneyMask,
              }}
        InputLabelProps={{ shrink: true }}
              error={Boolean(touched.amount && errors.amount)}
              fullWidth
              helperText={touched.amount && t(errors.amount)}
              label={
                isLoadingPayoffAmount
                  ? t('COMMON.LOADING')
                  : t('COMMON.AMOUNT_WITH_IVA')
              }
              margin="normal"
              name="amount"
              value={values.amount}
              disabled={values.isPayoffRequest || isLoadingPayoffAmount}
              onChange={handleChange}
              onBlur={handleBlur}
              show
              variant="outlined"
            />

            <FormControl fullWidth>
              <DropdownMenu
                list={PaymentFormList}
                value={values.paymentForm}
                name="paymentForm"
                setValue={value => setFieldValue('paymentForm', value)}
                label={t('TRANSACTION.PAYMENT_FORM')}
                error={Boolean(touched.paymentForm && errors.paymentForm)}
                errorText={touched.paymentForm && t(errors.paymentForm)}
              />
            </FormControl>

            <FormControl fullWidth>
              <DatePicker
                name="date"
                label={t('TRANSACTION.DATE')}
                value={values.paymentDate || null}
                onChange={newValue => {
                  setFieldValue('paymentDate', momentLocalTimezone(newValue));
                }}
                slotProps={{
                  textField: {
                    sx: { width: '100%', margin: '16px 0' },
                    error: Boolean(touched.paymentDate && errors.paymentDate),
                    helperText: touched.paymentDate && t(errors.paymentDate),
                    FormHelperTextProps: { sx: { color: 'tomato' } },
                    InputProps: { autoComplete: 'off' }
                  }
                }}
              />
            </FormControl>

            <Box display="flex">
              <Button
                className={clsx(classes.buttonFirstStep, classes.buttonMain)}
                type="submit"
                color="primary"
                variant="outlined"
              >
                {t('COMMON.NEXT')}
              </Button>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};
StepOne.propTypes = {
  data: PropTypes.object,
  handleNext: PropTypes.func
};

export default StepOne;
